import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import { Link, Tooltip } from "@mui/material";
import { DataTable } from "@outpave/ds";
import type { DataColumn, RowAction } from "@outpave/ds";
import { toast } from "react-toastify";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Copy01Icon,
  Edit04Icon,
  Invoice01Icon,
  PlayCircleIcon,
  StopCircleIcon,
  ViewIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { GET_PAYMENT_LINKS } from "graphql/paymentLink";
import { DEFAULT_PAGINATION_SIZE } from "components/common/Constants";
import RenderPaymentLinkStatus from "components/common/RenderPaymentLinkStatus";
import { formatCashFull } from "utils/formatCash";
import { PaymentLinkStatus } from "enums/PaymentLink";
import { PaymentLink } from "hooks/usePaymentLinkData";
import DeactivatePaymentLinkModal from "./DeactivatePaymentLinkModal";
import PaymentLinkPaymentsDrawer from "./PaymentLinkPaymentsDrawer";
import { describePaymentLinkAmount } from "utils/paymentLinkAmount";

export type PaymentLinkFilterCriteriaProps = {
  status: PaymentLinkStatus[];
  searchTerm?: string;
};

type PaymentLinksTableProps = {
  filterCriteria: PaymentLinkFilterCriteriaProps;
  refreshIndex: number;
  readOnly: boolean;
  onViewDetails: (paymentLinkId: string) => void;
  onEdit: (paymentLinkId: string) => void;
  onRefresh: () => void;
};

const PaymentLinksTable: React.FC<PaymentLinksTableProps> = ({
  filterCriteria,
  refreshIndex,
  readOnly,
  onViewDetails,
  onEdit,
  onRefresh,
}) => {
  const { t } = useTranslation();

  const [paginationVariables, setPaginationVariables] = useState({});
  const [selectedLink, setSelectedLink] = useState<PaymentLink | null>(null);
  const [paymentsForLink, setPaymentsForLink] = useState<PaymentLink | null>(
    null,
  );
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  useEffect(() => {
    setPaginationVariables({
      first: DEFAULT_PAGINATION_SIZE,
      after: undefined,
      status: filterCriteria.status,
      searchTerm: filterCriteria.searchTerm,
    });
  }, [filterCriteria]);

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_PAYMENT_LINKS,
      initialVariables: paginationVariables,
      extractData: extractPaymentLinks,
      refetchIndex: refreshIndex,
    });

  const copyLink = async (link: PaymentLink) => {
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success(t("Link copied"));
    } catch (error) {
      toast.error(t("Could not copy the link"));
    }
  };

  const columns: DataColumn<PaymentLink>[] = useMemo(
    () => [
      {
        id: "name",
        header: t("Name"),
        maxWidth: 240,
        cell: (link) => link.name,
      },
      {
        id: "amount",
        header: t("Amount"),
        cell: (link) => describePaymentLinkAmount(link, t),
      },
      {
        id: "status",
        header: t("Status"),
        cell: (link) => (
          <RenderPaymentLinkStatus
            status={link.status}
            expiresAt={link.expiresAt}
          />
        ),
      },
      {
        id: "views",
        header: t("Sessions"),
        cell: (link) => link.stats?.views || 0,
      },
      {
        id: "sales",
        header: t("Sales"),
        cell: (link) =>
          link.stats?.sales ? (
            <Tooltip title={t("View payments")}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={(event) => {
                  event.stopPropagation();
                  setPaymentsForLink(link);
                }}
              >
                {link.stats.sales}
              </Link>
            </Tooltip>
          ) : (
            0
          ),
      },
      {
        id: "revenue",
        header: t("Revenue"),
        cell: (link) => formatCashFull(link.stats?.revenue || 0, link.currency),
      },
    ],
    [t],
  );

  const rowActions = (link: PaymentLink): RowAction[] => [
    {
      label: t("Copy link"),
      icon: <HugeiconsIcon icon={Copy01Icon} size={22} />,
      onClick: () => copyLink(link),
    },
    {
      label: t("View payments"),
      icon: <HugeiconsIcon icon={Invoice01Icon} size={22} />,
      onClick: () => setPaymentsForLink(link),
    },
    {
      label: t("View Details"),
      icon: <HugeiconsIcon icon={ViewIcon} size={22} />,
      onClick: () => onViewDetails(link.id),
    },
    ...(readOnly
      ? []
      : [
          {
            label: t("Edit"),
            icon: <HugeiconsIcon icon={Edit04Icon} size={22} />,
            onClick: () => onEdit(link.id),
          },
          link.status === PaymentLinkStatus.ACTIVE
            ? {
                label: t("Deactivate"),
                icon: <HugeiconsIcon icon={StopCircleIcon} size={22} />,
                onClick: () => {
                  setSelectedLink(link);
                  setDeactivateOpen(true);
                },
              }
            : {
                label: t("Reactivate"),
                icon: <HugeiconsIcon icon={PlayCircleIcon} size={22} />,
                onClick: () => {
                  setSelectedLink(link);
                  setDeactivateOpen(true);
                },
              },
        ]),
  ];

  return (
    <Box sx={{ position: "relative", height: "100%" }}>
      <DataTable<PaymentLink>
        columns={columns}
        rows={dataArray}
        getRowKey={(link) => link.id}
        loading={displayLoading}
        isFetchingMore={isInfiniteScrollFetching}
        onRowClick={(link) => onViewDetails(link.id)}
        onScroll={handleScroll}
        emptyState={t("No payment links found")}
        rowActions={rowActions}
        rowActionsLabel={t("RowActions")}
        loadingMoreText={t("Loading more payment links")}
        stickyHeader
      />

      <PaymentLinkPaymentsDrawer
        open={!!paymentsForLink}
        paymentLinkId={paymentsForLink?.id || null}
        paymentLinkName={paymentsForLink?.name}
        onClose={() => setPaymentsForLink(null)}
      />

      <DeactivatePaymentLinkModal
        open={deactivateOpen}
        paymentLink={selectedLink}
        onClose={() => setDeactivateOpen(false)}
        onDone={onRefresh}
      />
    </Box>
  );
};

function extractPaymentLinks(
  data: Record<string, { edges: { node: PaymentLink }[] }>,
): PaymentLink[] {
  const firstKey = Object.keys(data)[0];
  return data[firstKey]?.edges.map((edge) => edge.node) || [];
}

export default PaymentLinksTable;
